import { useCallback, useEffect, useState } from "react";
import {
  resolveMediaKindFromExtension,
  resolveMediaKindFromType,
  type MediaKind,
} from "../../../utils/mediaType";
import MediaLoadError from "./MediaLoadError";
import QuestionAudio from "./QuestionAudio";
import QuestionImg from "./QuestionImg";
import QuestionVideo from "./QuestionVideo";

export type QuestionMediaRendererProps = {
  media?: string;
  mediaType?: string;
  showPreviewTimer?: boolean;
  getPreviewTimerSeconds?: () => number;
  previewTimerInitialTime?: number;
  previewTimerElapsedMode?: boolean;
};

type RenderStrategy = "question_type" | "extension";

const QuestionMediaRenderer = ({
  media,
  mediaType,
  showPreviewTimer,
  getPreviewTimerSeconds,
  previewTimerInitialTime,
  previewTimerElapsedMode,
}: QuestionMediaRendererProps) => {
  const [strategy, setStrategy] = useState<RenderStrategy>("question_type");
  const [activeKind, setActiveKind] = useState<MediaKind>(() => resolveMediaKindFromType(mediaType));
  const [showManualRetry, setShowManualRetry] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    setStrategy("question_type");
    setActiveKind(resolveMediaKindFromType(mediaType));
    setShowManualRetry(false);
    setRetryKey(0);
    setIsRetrying(false);
  }, [media, mediaType]);

  const handleLoad = useCallback(() => {
    setShowManualRetry(false);
    setIsRetrying(false);
  }, []);

  const handleError = useCallback(() => {
    if (!media) {
      setShowManualRetry(true);
      setIsRetrying(false);
      return;
    }

    if (strategy === "question_type") {
      const extensionKind = resolveMediaKindFromExtension(media);
      if (extensionKind && extensionKind !== activeKind) {
        setStrategy("extension");
        setActiveKind(extensionKind);
        setShowManualRetry(false);
        setIsRetrying(false);
        return;
      }
    }

    setShowManualRetry(true);
    setIsRetrying(false);
  }, [activeKind, media, strategy]);

  const handleManualRetry = useCallback(() => {
    setIsRetrying(true);
    setShowManualRetry(false);
    setStrategy("question_type");
    setActiveKind(resolveMediaKindFromType(mediaType));
    setRetryKey((prev) => prev + 1);
  }, [mediaType]);

  if (!media || !activeKind) return null;

  if (showManualRetry) {
    return <MediaLoadError onRetry={handleManualRetry} isRetrying={isRetrying} />;
  }

  const childKey = `${activeKind}-${strategy}-${retryKey}`;

  if (activeKind === "image") {
    return (
      <QuestionImg
        key={childKey}
        media={media}
        retryKey={retryKey}
        onError={handleError}
        onLoad={handleLoad}
        showPreviewTimer={showPreviewTimer}
        getPreviewTimerSeconds={getPreviewTimerSeconds}
        previewTimerInitialTime={previewTimerInitialTime}
        previewTimerElapsedMode={previewTimerElapsedMode}
      />
    );
  }

  if (activeKind === "video") {
    return (
      <QuestionVideo
        key={childKey}
        media={media}
        retryKey={retryKey}
        onError={handleError}
        onLoad={handleLoad}
      />
    );
  }

  return (
    <QuestionAudio
      key={childKey}
      media={media}
      retryKey={retryKey}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
};

export default QuestionMediaRenderer;
